import { Link } from 'react-router-dom'

interface ArtifactCardProps {
  title: string
  subtitle?: string
  description?: string
  imageUrl: string
  linkTo: string
  tags?: Array<{ label: string; type?: 'archive' | 'theme' | 'region' | 'type' }>
  type?: 'default' | 'podcast' | 'video' | 'artifact'
  typeLabel?: string
  archiveKey?: string
  meta?: string
}

const archiveColors: Record<string, string> = {
  treaty: 'bg-blue-900/30 text-blue-300 border-blue-500/30',
  conflict: 'bg-red-900/30 text-red-300 border-red-500/30',
  civilization: 'bg-amber-900/30 text-amber-300 border-amber-500/30',
  origin: 'bg-emerald-900/30 text-emerald-300 border-emerald-500/30',
  lineage: 'bg-purple-900/30 text-purple-300 border-purple-500/30',
}

export default function ArtifactCard({
  title,
  subtitle,
  description,
  imageUrl,
  linkTo,
  tags = [],
  type = 'default',
  typeLabel,
  archiveKey,
  meta,
}: ArtifactCardProps) {
  const isExternal = linkTo.startsWith('http')

  const CardWrapper = ({ children }: { children: React.ReactNode }) => {
    if (isExternal) {
      return (
        <a
          href={linkTo}
          target="_blank"
          rel="noopener noreferrer"
          className="block artifact-frame group h-full"
        >
          {children}
        </a>
      )
    }
    return (
      <Link to={linkTo} className="block artifact-frame group h-full">
        {children}
      </Link>
    )
  }

  return (
    <CardWrapper>
      <article className="flex flex-col h-full">
        {/* Image/Medallion */}
        <div className="relative aspect-square bg-archive-slate flex items-center justify-center overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="medallion w-full h-full object-cover"
            loading="lazy"
          />
          {type !== 'default' && (
            <div className="absolute top-3 left-3">
              <span className="px-2 py-1 text-xs font-medium uppercase tracking-wider bg-archive-black/80 text-archive-muted rounded">
                {typeLabel ?? type}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 p-4 flex flex-col">
          {/* Archive badge */}
          {archiveKey && (
            <span
              className={`self-start mb-2 px-2 py-0.5 text-xs font-medium uppercase tracking-wider rounded border ${
                archiveColors[archiveKey] || 'bg-archive-stone/50 text-archive-muted border-archive-ash/30'
              }`}
            >
              {archiveKey}
            </span>
          )}

          {/* Title */}
          <h3 className="font-display text-archive-cream text-lg leading-tight group-hover:text-archive-gold transition-colors">
            {title}
          </h3>

          {/* Subtitle */}
          {subtitle && (
            <p className="mt-1 text-sm text-archive-muted">{subtitle}</p>
          )}

          {/* Description */}
          {description && (
            <p className="mt-2 text-sm text-archive-text/80 line-clamp-2 flex-1">
              {description}
            </p>
          )}

          {/* Meta */}
          {meta && (
            <p className="mt-2 text-xs text-archive-muted">{meta}</p>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {tags.slice(0, 3).map((tag, idx) => (
                <span key={idx} className="tag-pill text-[10px]">
                  {tag.label}
                </span>
              ))}
              {tags.length > 3 && (
                <span className="tag-pill text-[10px]">+{tags.length - 3}</span>
              )}
            </div>
          )}
        </div>
      </article>
    </CardWrapper>
  )
}
